/**
 * Public site settings service.
 *
 * Loads the public-safe settings from GET /settings/public once and caches them,
 * so the whole app (maintenance gate + branding) shares a single request.
 * Subscribers are notified whenever the settings are re-fetched, which lets an
 * admin saving changes update every open tab live.
 */
import { apiGet, isApiConfigured } from "./http";
import type { PublicSiteSettings } from "@/types/site";

export const DEFAULT_PUBLIC_SETTINGS: PublicSiteSettings = {
  siteName: "Coastal Bizz Connect",
  tagline: "",
  maintenanceMode: false,
  supportEmail: "",
  supportPhone: "",
  address: "",
  currency: "INR",
  facebook: "",
  instagram: "",
  twitter: "",
  youtube: "",
  faviconUrl: "",
  fontUrl: "",
  fontFamily: "",
};

let cache: PublicSiteSettings | null = null;
let inflight: Promise<PublicSiteSettings> | null = null;
const listeners = new Set<(settings: PublicSiteSettings) => void>();

export function getCachedPublicSettings(): PublicSiteSettings | null {
  return cache;
}

export function fetchPublicSettings(): Promise<PublicSiteSettings> {
  if (!isApiConfigured()) return Promise.resolve(DEFAULT_PUBLIC_SETTINGS);
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = apiGet<Partial<PublicSiteSettings>>("/settings/public")
    .then((raw) => {
      cache = { ...DEFAULT_PUBLIC_SETTINGS, ...raw };
      return cache;
    })
    .catch(() => {
      // Never let a settings failure break the site — fall back to defaults.
      cache = { ...DEFAULT_PUBLIC_SETTINGS };
      return cache as PublicSiteSettings;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export function subscribePublicSettings(fn: (settings: PublicSiteSettings) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Drop the cache and re-fetch, notifying every subscriber with the fresh values. */
export function refreshPublicSettings(): Promise<PublicSiteSettings> {
  cache = null;
  inflight = null;
  return fetchPublicSettings().then((settings) => {
    listeners.forEach((fn) => fn(settings));
    return settings;
  });
}

/** Called after an admin saves System Settings so branding/maintenance apply instantly. */
export function notifyPublicSettingsChanged(): void {
  void refreshPublicSettings();
}